import { useDispatch, useSelector, useStore, shallowEqual } from "react-redux";
import {
	deleteMatch as deleteMatchAction,
	editMatch as editMatchAction,
	getMatches as getMatchesAction,
	saveMatch as saveMatchAction
} from "./actions";
import { useGameContextContext } from "../game-context/context";
import { getSafeOrDefault } from "../../util/object-util";

const useMatchesContext = () => {
	const store = useStore();
	const dispatch = useDispatch();
	const { state: gameContextState } = useGameContextContext();
	const { finalizeOrder } = gameContextState.finalizeSettings();

	const useMatches = () => useSelector((state) => state.matches.matches);

	const useTotals = () => useSelector((state) => state.matches.totals);

	const useFinalizedSingles = () =>
		useSelector((state) =>
			getSafeOrDefault(
				() =>
					finalizeOrder?.singles.map((id) =>
						state.matches.matches
							.filter((m) => m.isSingle)
							.find((single) => single.id === id)
					),
				[]
			)
		);

	const useFinalizedDoubles = () =>
		useSelector((state) =>
			getSafeOrDefault(
				() =>
					finalizeOrder?.doubles.map((id) =>
						state.matches.matches
							.filter((m) => !m.isSingle)
							.find((double) => double.id === id)
					),
				[]
			)
		);

	const useSingles = () =>
		useSelector((state) =>
			state.matches.matches
				.filter((m) => m.isSingle)
				.sort((a, b) => a.matchNumber - b.matchNumber)
		);

	const useDoubles = () =>
		useSelector((state) =>
			state.matches.matches
				.filter((m) => !m.isSingle)
				.sort((a, b) => a.matchNumber - b.matchNumber)
		);

	const useCurrentSet = () => useSelector(({ matches }) => matches.currentSet);

	const useSingleParticipants = () =>
		useSelector((state) => {
			return [
				...new Set(
					state.matches.matches
						.filter((m) => m.isSingle)
						.map((match) => [...match.away.players, ...match.home.players])
						.flat()
				)
			];
		}, shallowEqual);

	const useDoubleParticipants = () =>
		useSelector((state) => {
			return [
				...new Set(
					state.matches.matches
						.filter((m) => !m.isSingle)
						.map((match) => [...match.away.players, ...match.home.players])
						.flat()
				)
			];
		}, shallowEqual);

	const editMatch = async (match) => {
		const { currentEvent } = store.getState().events;
		await editMatchAction(match, currentEvent.id, dispatch);
	};

	const saveMatch = async (match) => {
		const { currentEvent } = store.getState().events;
		await saveMatchAction(match, currentEvent.id, dispatch);
	};

	const getMatches = (eventId) => {
		getMatchesAction(eventId, dispatch);
	};

	const deleteMatch = async (match) => {
		await deleteMatchAction(match, dispatch);
	};

	const state = {
		matches: useMatches,
		totals: useTotals,
		singles: useSingles,
		doubles: useDoubles,
		currentSet: useCurrentSet,
		singleParticipants: useSingleParticipants,
		doubleParticipants: useDoubleParticipants,
		finalizedSingles: useFinalizedSingles,
		finalizedDoubles: useFinalizedDoubles
	};

	return {
		state,
		saveMatch,
		deleteMatch,
		getMatches,
		editMatch
	};
};

export { useMatchesContext };
